/**
 * usePan Hook
 * 
 * Manages panning/dragging state for map navigation.
 * Handles drag interactions and wheel-based panning.
 */

import { useState, useCallback, useEffect } from 'react';
import { AxialCoord } from '../types/terrain';

// ============================================================================
// Types
// ============================================================================

export interface UsePanOptions {
  center: AxialCoord;
  panSpeed?: number;
}

export interface UsePanReturn {
  offset: { x: number; y: number };
  isDragging: boolean;
  hasDragged: boolean;
  handleMouseDown: (e: React.MouseEvent) => void;
  handleMouseMove: (e: React.MouseEvent) => void;
  handleMouseUp: () => void;
  handleMouseLeave: () => void;
  handleWheel: (e: React.WheelEvent) => void;
  resetOffset: () => void;
}

// ============================================================================
// Hook
// ============================================================================

export function usePan(options: UsePanOptions): UsePanReturn {
  const { center, panSpeed = 1.5 } = options;

  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState<{ x: number; y: number } | null>(null);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [hasDragged, setHasDragged] = useState(false);

  // Reset offset when center changes
  useEffect(() => {
    setOffset({ x: 0, y: 0 });
  }, [center.q, center.r]);

  const resetOffset = useCallback(() => {
    setOffset({ x: 0, y: 0 });
  }, []);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    setIsDragging(true);
    setDragStart({ x: e.clientX, y: e.clientY });
    setHasDragged(false); // Reset drag flag on new mouse down
  }, []);

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    // Handle dragging
    if (isDragging && dragStart) {
      const dx = e.clientX - dragStart.x;
      const dy = e.clientY - dragStart.y;
      
      // If any movement occurred, mark as dragged
      if (Math.abs(dx) > 2 || Math.abs(dy) > 2) {
        setHasDragged(true);
      }
      
      setOffset(prev => ({ x: prev.x + dx, y: prev.y + dy }));
      setDragStart({ x: e.clientX, y: e.clientY });
    }
  }, [isDragging, dragStart]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
    setDragStart(null);
    // Reset hasDragged after a short delay (after click event has fired)
    setTimeout(() => setHasDragged(false), 10);
  }, []);

  const handleMouseLeave = useCallback(() => {
    setIsDragging(false);
    setDragStart(null);
  }, []);

  const handleWheel = useCallback((e: React.WheelEvent) => {
    // PAN MODE (Scroll without Shift)
    if (e.shiftKey) return; // Let zoom handle shift+scroll
    
    setOffset(prev => ({
      x: prev.x - e.deltaX * panSpeed,
      y: prev.y - e.deltaY * panSpeed,
    }));
  }, [panSpeed]);

  return {
    offset,
    isDragging,
    hasDragged,
    handleMouseDown,
    handleMouseMove,
    handleMouseUp,
    handleMouseLeave,
    handleWheel,
    resetOffset,
  };
}

